import React from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { VideosLayout } from "@/components/videos/VideosLayout";
import { useVideosData } from "@/hooks/useVideosData";
import { ArrowRight, GraduationCap } from "lucide-react";

export const VideosPlaylistsPage: React.FC = () => {
  const { collections, loading, error, refetch } = useVideosData();

  return (
    <VideosLayout loading={loading} error={error} onRetry={refetch}>
      <SEOHead
        title="Courses & Playlists | JNN Homes Northern Virginia"
        description="Curated video courses and playlists from JNN Homes covering vacation rental management, hosting tips, and home setups."
        canonical="https://www.jnnhomes.com/videos/playlists"
      />

      <div className="space-y-8">
        <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium">
          <Link to="/videos" className="hover:text-[#9A5207]">
            Videos
          </Link>
          <span>›</span>
          <span className="text-slate-900 font-semibold">
            Courses & playlists
          </span>
        </nav>

        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">
            Courses & playlists
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Step-by-step video courses to help you host with confidence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {collections.map((col) => {
            return (
              <Link
                key={col.slug}
                to={`/videos/course/${col.slug}`}
                className="p-4 rounded-2xl border border-slate-200 bg-white hover:border-[#E8850F] transition-all group flex flex-col justify-between space-y-4 shadow-xs"
              >
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-1.5 rounded-xl overflow-hidden bg-slate-100 p-1">
                    {col.lessons.slice(0, 3).map((vId, idx) => (
                      <div
                        key={idx}
                        className="aspect-[9/16] bg-slate-200 rounded-lg overflow-hidden"
                      >
                        <img
                          src={`https://i.ytimg.com/vi/${vId}/hqdefault.jpg`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-[#E8850F]/14 text-[#9A5207]">
                        {col.category}
                      </span>
                      <span className="text-xs font-semibold text-slate-500">
                        {col.lessonCount} lessons
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-slate-900 group-hover:text-[#9A5207] transition-colors leading-snug">
                      {col.title}
                    </h3>
                    <p className="text-xs text-slate-600 line-clamp-2 mt-1">
                      {col.description}
                    </p>
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-[#9A5207]">
                  <span>Start watching</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}

          {/* Academy Coming Soon card */}
          <Link
            to="/videos/academy"
            className="p-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 hover:border-[#E8850F] transition-all group flex flex-col justify-between space-y-4"
          >
            <div className="space-y-3">
              <div className="aspect-[16/9] rounded-xl bg-slate-200/60 flex items-center justify-center">
                <GraduationCap className="w-10 h-10 text-slate-400" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-slate-200 text-slate-600">
                    Academy
                  </span>
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-amber-100 text-amber-700">
                    Coming soon
                  </span>
                </div>
                <h3 className="text-base font-bold text-slate-900 group-hover:text-[#9A5207] transition-colors leading-snug">
                  JNN Homes Academy
                </h3>
                <p className="text-xs text-slate-600 line-clamp-2 mt-1">
                  Step-by-step lessons on preparing, furnishing and caring for a
                  home as a vacation rental.
                </p>
              </div>
            </div>
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs font-semibold text-slate-500">
              <span>Learn more</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>
      </div>
    </VideosLayout>
  );
};
